import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { organiserExhibitorsQueryKeys } from "../query-keys";
import productQueryKeys from "../query-keys/product.query-keys";
import { UpdateStructureForm } from "../schemas";

const organiserUpdateStructure = async (
  id: string,
  body: UpdateStructureForm
) => {
  try {
    const { data } = await privateAxios.patch(
      `/organiser-handle-structure/${id}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useOrganiserUpdateStructureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStructureForm }) =>
      organiserUpdateStructure(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserExhibitorsQueryKeys.all,
      });
    },
  });
};

const organiserDeleteStructureProduct = async (productId: string) => {
  const { data } = await privateAxios.delete(
    `/organiser-handle-structure/products/${productId}`
  );
  return data;
};

export const useOrganiserDeleteStructureProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      organiserDeleteStructureProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [productQueryKeys.all],
      });
    },
  });
};

const organiserCreateStructureProduct = async (body: {
  structureId: string;
  name: string;
  eventId: string;
  images: { key: string; name: string; type: string }[];
  description?: string;
  link?: string;
}) => {
  try {
    const { data } = await privateAxios.post(
      `/organiser-handle-structure/products`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useOrganiserCreateStructureProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organiserCreateStructureProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [productQueryKeys.all],
      });
    },
  });
};

const organiserUpdateStructureProduct = async (
  productId: string,
  body: {
    name: string;
    images: { key: string; name: string; type: string }[];
    description?: string;
    link?: string;
  }
) => {
  try {
    const { data } = await privateAxios.patch(
      `/organiser-handle-structure/products/${productId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useOrganiserUpdateStructureProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: {
        name: string;
        images: { key: string; name: string; type: string }[];
        description?: string;
        link?: string;
      };
    }) => organiserUpdateStructureProduct(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [productQueryKeys.all],
      });
    },
  });
};

const organiserImportProducts = async (body: {
  structureId: string;
  eventId: string;
  add: { name: string }[];
  delete: { id: string }[];
}) => {
  const { data } = await privateAxios.post(
    `/organiser-handle-structure/products/import`,
    body
  );
  return data;
};

export const useOrganiserImportProductsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organiserImportProducts,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [productQueryKeys.all],
      });
    },
  });
};

export const organiserGetStructuresQrCodes = async (
  eventId: string,
  saveAs: (file: File) => void
) => {
  const { data } = await privateAxios.get<Blob>(
    `/organiser-handle-structure/structures-qr-codes/${eventId}`,
    {
      responseType: "blob",
    }
  );

  const file = new File([data], "structures-qr-codes.zip", {
    type: "application/zip",
  });

  saveAs(file);
};
